// AI Build Lab Post-Training Follow-Up Hub — Phase 1
// Public landing page at /build-lab-follow-up
// Vibe: warm, celebratory, post-lab. Soft peach/amber/cream gradients with
// confetti-style accents. Cobalt #1A2DD7 reserved for functional CTAs only.
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  RadioGroup, RadioGroupItem,
} from "@/components/ui/radio-group";
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from "@/components/ui/card";
import {
  Loader2, Sparkles, ClipboardCheck, ExternalLink, Users, Calendar,
  BookOpen, HelpCircle, Star, Camera, ArrowRight, CheckCircle2,
  PartyPopper, Gift, Map, Layers, Zap, Server, MessageSquareQuote,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Subtle scattered dots — kept small + muted to fit the editorial bone+cobalt
// aesthetic of donjawilliams.com (no celebration confetti).
function ConfettiBackdrop() {
  const dots = [
    { left: "8%",  top: "14%", color: "#1A2DD7", size: 6,  opacity: 0.35 },
    { left: "92%", top: "10%", color: "#0A0A0A", size: 4,  opacity: 0.18 },
    { left: "18%", top: "78%", color: "#1A2DD7", size: 5,  opacity: 0.22 },
    { left: "85%", top: "82%", color: "#0A0A0A", size: 6,  opacity: 0.16 },
    { left: "48%", top: "8%",  color: "#1A2DD7", size: 4,  opacity: 0.25 },
    { left: "12%", top: "44%", color: "#0A0A0A", size: 4,  opacity: 0.14 },
    { left: "88%", top: "48%", color: "#1A2DD7", size: 7,  opacity: 0.20 },
    { left: "42%", top: "88%", color: "#1A2DD7", size: 4,  opacity: 0.30 },
  ];
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden>
      {dots.map((d, i) => (
        <span
          key={i}
          className="absolute rounded-full"
          style={{ left: d.left, top: d.top, width: d.size, height: d.size, background: d.color, opacity: d.opacity }}
        />
      ))}
    </div>
  );
}

const NEXT_BUILD_OPTIONS = [
  ["ai_assisted_website", "AI-Assisted Website"],
  ["landing_page", "Landing Page"],
  ["lead_funnel", "Lead Funnel"],
  ["simple_app_prototype", "Simple App Prototype"],
  ["customer_intake_system", "Customer Intake System"],
  ["dashboard", "Dashboard"],
  ["not_sure", "Not sure yet"],
];

const MAPPING_OPTIONS = [
  ["yes", "Yes"],
  ["somewhat", "Somewhat"],
  ["no", "No"],
];

const EMERGENT_OPTIONS = [
  ["yes", "Yes"],
  ["not_yet", "Not yet"],
  ["need_link", "I need the link again"],
];

const SUPPORT_OPTIONS = [
  ["build_on_own", "I want to build on my own"],
  ["group_troubleshooting", "I need group troubleshooting support"],
  ["review_process_or_prompt", "I need Donja to review my process map or prompt"],
  ["private_1on1", "I want a private 1:1 Build-With-You session"],
  ["built_for_me", "I want it built for me"],
  ["need_guidance", "I am not sure and need guidance"],
];

const TESTIMONIAL_OPTIONS = [
  ["written", "Written testimonial"],
  ["video", "Video testimonial"],
  ["maybe_later", "Maybe later"],
  ["no", "No"],
];

export default function BuildLabFollowUp() {
  const [config, setConfig] = useState(null);
  const [testimonialOpen, setTestimonialOpen] = useState(false);
  const [buildShareOpen, setBuildShareOpen] = useState(false);

  useEffect(() => {
    axios.get(`${API}/build-lab-followup/config`).then((r) => setConfig(r.data)).catch(() => setConfig({}));
  }, []);

  return (
    <div className="min-h-screen bg-bone text-ink">
      <Hero />
      <main className="max-w-3xl mx-auto px-4 pb-24 space-y-12">
        <SurveyCard />
        <Section
          icon={<Sparkles size={14} />}
          eyebrow="Step 02"
          title="Sign up for Emergent"
          body="If you haven't already, this is the platform we used live during the lab. The link below uses Donja's affiliate so it directly supports the work — no extra cost to you."
          ctaLabel={config?.emergent_affiliate_url ? "Sign up for Emergent" : "Coming soon — Donja will share the link"}
          ctaHref={config?.emergent_affiliate_url}
        />
        <Section
          icon={<Users size={14} />}
          eyebrow="Step 03 · Group Support"
          title="Join the $97/month Group AI Build Support Clinic"
          body="A monthly live troubleshooting clinic where Donja reviews your prompts, debugs your build, and helps you move past the spot you're stuck on. Built for people who don't want to figure it out alone."
          ctaLabel={config?.clinic_stripe_url ? "Join the Clinic — $97/month" : "Spots open soon"}
          ctaHref={config?.clinic_stripe_url}
        />
        <Section
          icon={<Calendar size={14} />}
          eyebrow="Step 04 · Private 1:1"
          title="Book a Private Done-For-You Build session"
          body="Donja sits with you in your tool, builds alongside you, and ships a working asset to your real deadline. Best for people who need it done, not explained."
          ctaLabel={config?.booking_url ? "Reserve my Done-For-You session" : "Booking opens soon"}
          ctaHref={config?.booking_url}
        />

        <SectionBand title="More support, when you're ready" />

        <div className="grid sm:grid-cols-2 gap-5">
          <OfferTile
            icon={<Map size={16} />}
            eyebrow="Template"
            title="Process Map = Prompt"
            body="The template that turns your process map into a working prompt — the exact framework from the lab."
            ctaLabel={config?.process_map_template_url ? "Get the template" : "Coming soon"}
            ctaHref={config?.process_map_template_url}
          />
          <OfferTile
            icon={<Layers size={16} />}
            eyebrow="Blueprint"
            title="Process Map + Prompt Blueprint"
            body="The full blueprint — process map walkthroughs, prompt library, and the system Donja uses with clients."
            ctaLabel={config?.process_map_blueprint_url ? "Get the blueprint" : "Coming soon"}
            ctaHref={config?.process_map_blueprint_url}
          />
          <OfferTile
            icon={<Zap size={16} />}
            eyebrow="Done-For-You"
            title="Done-For-You Starter Build"
            body="A focused, fixed-scope starter build — the fastest way to ship one real thing without doing it alone."
            ctaLabel={config?.done_for_you_starter_url ? "Reserve a Starter Build" : "Coming soon"}
            ctaHref={config?.done_for_you_starter_url}
          />
          <OfferTile
            icon={<Server size={16} />}
            eyebrow="$525 · Migration"
            title="Move Code Off Emergent to Your Own Hosting"
            body="Donja moves your Emergent build to your own hosting platform so you own the code, the domain, and the long-term cost."
            ctaLabel={config?.move_code_off_emergent_url ? "Move my build — $525" : "Coming soon"}
            ctaHref={config?.move_code_off_emergent_url}
          />
        </div>

        <SectionBand title="Resources from the lab" />

        <div className="grid sm:grid-cols-2 gap-5">
          <ResourceCard
            icon={<BookOpen size={16} />}
            title="Participant Guide"
            body="The complete walkthrough from the lab — bookmark it."
            ctaLabel={config?.participant_guide_url ? "Download" : "Coming soon"}
            ctaHref={config?.participant_guide_url}
          />
          <ResourceCard
            icon={<HelpCircle size={16} />}
            title="FAQ Guide"
            body="Quick answers to the questions everyone asks after the lab."
            ctaLabel={config?.faq_guide_url ? "Download" : "Coming soon"}
            ctaHref={config?.faq_guide_url}
          />
        </div>

        <SectionBand title="Show up for the community" />

        <div className="grid sm:grid-cols-2 gap-5">
          <ActionTile
            icon={<MessageSquareQuote size={16} />}
            eyebrow="Testimonial"
            title="Submit a testimonial"
            body="A sentence or a Loom — your words help future participants take the leap. Written or video, your call."
            onClick={() => setTestimonialOpen(true)}
            testid="open-testimonial-modal"
          />
          <ActionTile
            icon={<Camera size={16} />}
            eyebrow="Share"
            title="Share what you built"
            body="Drop a link, a screenshot, or a story of what you started during (or after) the lab. The community gets inspired by yours."
            onClick={() => setBuildShareOpen(true)}
            testid="open-build-share-modal"
          />
        </div>

        <Footer />
      </main>

      <TestimonialModal open={testimonialOpen} setOpen={setTestimonialOpen} />
      <BuildShareModal open={buildShareOpen} setOpen={setBuildShareOpen} />
    </div>
  );
}

// ---------- Hero ----------
function Hero() {
  return (
    <header className="relative overflow-hidden bg-bone">
      <ConfettiBackdrop />
      <div className="relative max-w-3xl mx-auto px-4 pt-16 sm:pt-24 pb-12 text-center">
        <div className="eyebrow inline-flex items-center gap-2" data-testid="hero-eyebrow">
          <PartyPopper size={11} /> You did the thing
        </div>
        <h1 className="mt-5 font-display text-4xl sm:text-5xl md:text-6xl leading-[1.05] tracking-[-0.025em] text-ink">
          Thank you for joining<br /><span className="italic">the AI Build Lab.</span>
        </h1>
        <p className="mt-5 text-base sm:text-lg text-ink/75 max-w-xl mx-auto leading-relaxed">
          Two hours of live building together — and now the part nobody talks about: <span className="text-ink font-medium">what happens next.</span> Take a breath, then pick the path below that matches where you are right now.
        </p>
        <div className="mt-8 flex items-center justify-center gap-2 text-xs text-ink/55 font-mono-spec uppercase tracking-[0.18em]" data-testid="hero-from">
          <Gift size={12} />
          <span>With love, from Donja Williams &middot; Freedom In Me LLC</span>
        </div>
      </div>
      <div className="hairline max-w-3xl mx-auto" />
    </header>
  );
}

// ---------- Reusable section card ----------
function Section({ icon, eyebrow, title, body, ctaLabel, ctaHref, accent }) {
  const disabled = !ctaHref;
  return (
    <section className="relative">
      <div
        className="absolute -left-2 top-7 hidden sm:block w-1 h-10 rounded-full"
        style={{ background: accent || "var(--brand)" }}
        aria-hidden
      />
      <Card className="border-[var(--line)] bg-white rounded-2xl shadow-[0_1px_0_rgba(0,0,0,0.02)] overflow-hidden">
        <CardHeader className="pb-2">
          <div className="flex items-center gap-2 eyebrow !mb-0" style={{ color: accent || "var(--brand)" }}>
            <span>{icon}</span>
            {eyebrow}
          </div>
          <CardTitle className="font-display text-2xl sm:text-[28px] tracking-[-0.022em] mt-2 leading-snug text-ink">{title}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-ink/75 leading-relaxed text-[15px]">{body}</p>
          <div className="mt-5">
            <Button
              asChild={!disabled}
              disabled={disabled}
              className="btn-ink rounded-full h-11 px-6 font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid={`cta-${(eyebrow || title).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            >
              {disabled ? (
                <span>{ctaLabel}</span>
              ) : (
                <a href={ctaHref} target={ctaHref.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer">
                  {ctaLabel} <ArrowRight size={14} className="ml-1.5" />
                </a>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </section>
  );
}

function ResourceCard({ icon, title, body, ctaLabel, ctaHref, accent }) {
  const disabled = !ctaHref;
  return (
    <Card className="border-[var(--line)] bg-white rounded-2xl shadow-[0_1px_0_rgba(0,0,0,0.02)]">
      <CardContent className="p-6">
        <div className="inline-flex items-center justify-center w-10 h-10 rounded-full mb-3" style={{ background: "var(--brand-soft)", color: "var(--brand)" }}>
          {icon}
        </div>
        <h3 className="font-display text-xl tracking-[-0.015em] text-ink">{title}</h3>
        <p className="mt-2 text-sm text-ink/70 leading-relaxed">{body}</p>
        <Button
          asChild={!disabled}
          disabled={disabled}
          variant="outline"
          className="btn-outline-ink mt-4 rounded-full h-9 px-4 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid={`resource-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
        >
          {disabled ? (
            <span>{ctaLabel}</span>
          ) : (
            <a href={ctaHref} target="_blank" rel="noopener noreferrer">
              {ctaLabel} <ExternalLink size={12} className="ml-1.5" />
            </a>
          )}
        </Button>
      </CardContent>
    </Card>
  );
}

// ---------- Survey card ----------
const INITIAL_SURVEY = {
  participant_name: "",
  email: "",
  business_name: "",
  attended_to_build: "",
  next_build_interest: "",
  star_rating: 0,
  most_valuable_part: "",
  unclear_or_overwhelming: "",
  mapping_understanding: "",
  emergent_signup_status: "",
  support_need: "",
  wants_follow_up: "",
  testimonial_choice: "",
  build_link: "",
  future_build_interest: "",
  permission_granted: false,
};

function SurveyCard() {
  const [form, setForm] = useState(INITIAL_SURVEY);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const requiredFilled = useMemo(() => {
    return (
      form.participant_name.trim() &&
      form.email.trim() &&
      form.attended_to_build.trim() &&
      form.next_build_interest &&
      form.star_rating > 0 &&
      form.most_valuable_part.trim() &&
      form.mapping_understanding &&
      form.emergent_signup_status &&
      form.support_need &&
      form.wants_follow_up &&
      form.testimonial_choice
    );
  }, [form]);

  const update = (k) => (v) => setForm((s) => ({ ...s, [k]: typeof v === "object" && v?.target ? v.target.value : v }));

  async function submit(e) {
    e.preventDefault();
    if (!requiredFilled) { toast.error("Please answer the required questions before submitting."); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/build-lab-followup/survey`, {
        ...form,
        referrer: typeof document !== "undefined" ? document.referrer.slice(0, 300) : "",
      });
      setDone(true);
      toast.success("Thank you — your feedback is in.");
      window.scrollTo({ top: document.getElementById("survey-anchor")?.offsetTop || 0, behavior: "smooth" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  if (done) {
    return (
      <section id="survey-anchor" className="relative">
        <Card className="border-[var(--line)] bg-white rounded-2xl">
          <CardContent className="py-12 px-6 sm:px-10 text-center">
            <div className="mx-auto mb-4 w-12 h-12 rounded-full bg-brand-soft flex items-center justify-center">
              <CheckCircle2 size={24} className="text-brand" />
            </div>
            <h3 className="font-display text-2xl sm:text-3xl tracking-[-0.02em] text-ink" data-testid="survey-thanks-heading">
              Thank you for completing the AI Build Lab survey.
            </h3>
            <p className="mt-5 text-ink/75 max-w-lg mx-auto leading-relaxed">
              Your feedback has been received. Remember, you do not have to build alone. The clearer your process, the stronger your prompt. The stronger your prompt, the better your build.
            </p>
            <p className="mt-5 text-ink max-w-lg mx-auto leading-relaxed font-medium">
              Please check your email for your next-step resources and support options.
            </p>
          </CardContent>
        </Card>
      </section>
    );
  }

  return (
    <section id="survey-anchor" className="relative">
      <div className="absolute -left-2 top-7 hidden sm:block w-1 h-10 rounded-full bg-brand" aria-hidden />
      <Card className="border-[var(--line)] bg-white rounded-2xl overflow-hidden">
        <CardHeader className="pb-2">
          <div className="flex items-center gap-2 eyebrow !mb-0">
            <ClipboardCheck size={12} />
            Step 01 &middot; About 3 minutes
          </div>
          <CardTitle className="font-display text-2xl sm:text-[28px] tracking-[-0.022em] mt-2 leading-snug text-ink">AI Build Lab Participant Survey</CardTitle>
          <CardDescription className="text-ink/75 text-[15px] leading-relaxed mt-1">
            Thank you for attending the AI Build Lab. Your feedback helps us improve the experience, understand what you are building, and know how to support you next.
            <br /><br />
            This should only take a few minutes. Please answer honestly so we can help you move from idea to process, from process to prompt, and from prompt to build.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-7" data-testid="survey-form">
            <Field label="Participant name *">
              <Input value={form.participant_name} onChange={(e) => setForm((s) => ({ ...s, participant_name: e.target.value }))} data-testid="survey-name" className="bg-white border-[var(--line)] focus-visible:ring-brand focus-visible:ring-offset-0" />
            </Field>
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Email address *">
                <Input type="email" value={form.email} onChange={(e) => setForm((s) => ({ ...s, email: e.target.value }))} data-testid="survey-email" className="bg-white border-[var(--line)] focus-visible:ring-brand focus-visible:ring-offset-0" />
              </Field>
              <Field label="Business name (optional)">
                <Input value={form.business_name} onChange={(e) => setForm((s) => ({ ...s, business_name: e.target.value }))} data-testid="survey-business" className="bg-white border-[var(--line)] focus-visible:ring-brand focus-visible:ring-offset-0" />
              </Field>
            </div>

            <Field label="What did you attend the AI Build Lab hoping to build? *">
              <Textarea rows={3} value={form.attended_to_build} onChange={(e) => setForm((s) => ({ ...s, attended_to_build: e.target.value }))} placeholder="A website, an intake form, a dashboard for my team..." data-testid="survey-attended" className="bg-white border-[var(--line)] focus-visible:ring-brand focus-visible:ring-offset-0" />
            </Field>

            <Field label="Which build are you most interested in completing next? *">
              <RadioPills value={form.next_build_interest} onChange={update("next_build_interest")} options={NEXT_BUILD_OPTIONS} testidPrefix="survey-next-build" />
            </Field>

            <Field label="How would you rate your overall AI Build Lab experience? *">
              <StarPicker value={form.star_rating} onChange={(n) => setForm((s) => ({ ...s, star_rating: n }))} />
            </Field>

            <Field label="What was the most valuable part of the training? *">
              <Textarea rows={3} value={form.most_valuable_part} onChange={(e) => setForm((s) => ({ ...s, most_valuable_part: e.target.value }))} data-testid="survey-valuable" className="bg-white border-[var(--line)] focus-visible:ring-brand focus-visible:ring-offset-0" />
            </Field>

            <Field label="What still feels unclear, overwhelming, or unfinished for you? (optional)">
              <Textarea rows={3} value={form.unclear_or_overwhelming} onChange={(e) => setForm((s) => ({ ...s, unclear_or_overwhelming: e.target.value }))} data-testid="survey-unclear" className="bg-white border-[var(--line)] focus-visible:ring-brand focus-visible:ring-offset-0" />
            </Field>

            <Field label="Did the training help you understand why you should map your process before building in Emergent? *">
              <RadioPills value={form.mapping_understanding} onChange={update("mapping_understanding")} options={MAPPING_OPTIONS} testidPrefix="survey-mapping" />
            </Field>

            <Field label="Have you signed up for Emergent using Donja's affiliate link? *">
              <RadioPills value={form.emergent_signup_status} onChange={update("emergent_signup_status")} options={EMERGENT_OPTIONS} testidPrefix="survey-emergent" />
            </Field>

            <Field label="What support do you need next? *">
              <RadioStack value={form.support_need} onChange={update("support_need")} options={SUPPORT_OPTIONS} testidPrefix="survey-support" />
            </Field>

            <Field label="Would you like someone to follow up with you? *">
              <RadioPills value={form.wants_follow_up} onChange={update("wants_follow_up")} options={[["yes", "Yes"], ["no", "No"]]} testidPrefix="survey-followup" />
            </Field>

            <Field label="Would you be willing to provide a testimonial? *">
              <RadioPills value={form.testimonial_choice} onChange={update("testimonial_choice")} options={TESTIMONIAL_OPTIONS} testidPrefix="survey-testimonial" />
            </Field>

            <Field label="If you already started building, paste your site, app, landing page, dashboard, or prototype link here. (optional)">
              <Input
                type="url"
                value={form.build_link}
                onChange={(e) => setForm((s) => ({ ...s, build_link: e.target.value }))}
                placeholder="https://your-build.com"
                data-testid="survey-build-link"
                className="bg-white border-[var(--line)] focus-visible:ring-brand focus-visible:ring-offset-0"
              />
            </Field>

            <Field label="What would you love to build next with support? (optional)">
              <Textarea
                rows={3}
                value={form.future_build_interest}
                onChange={(e) => setForm((s) => ({ ...s, future_build_interest: e.target.value }))}
                placeholder="A client portal, an automated booking system, an AI assistant for my members..."
                data-testid="survey-future-build"
                className="bg-white border-[var(--line)] focus-visible:ring-brand focus-visible:ring-offset-0"
              />
            </Field>

            <div className="rounded-xl border border-[var(--line)] bg-bone p-4">
              <label className="flex items-start gap-3 cursor-pointer">
                <Checkbox
                  checked={form.permission_granted}
                  onCheckedChange={(v) => setForm((s) => ({ ...s, permission_granted: !!v }))}
                  data-testid="survey-permission"
                  className="mt-0.5 border-brand data-[state=checked]:bg-brand"
                />
                <span className="text-sm text-ink/80 leading-relaxed">
                  I give Freedom In Me LLC permission to use my testimonial, name, business name, website/app screenshots, and/or sample build for marketing, training, and promotional purposes.
                </span>
              </label>
            </div>

            <Button type="submit" disabled={submitting || !requiredFilled} data-testid="survey-submit" className="btn-ink w-full rounded-full h-12 font-semibold text-base disabled:opacity-50 disabled:cursor-not-allowed">
              {submitting ? <Loader2 size={16} className="animate-spin" /> : (<>Submit my survey <ArrowRight size={14} className="ml-1.5" /></>)}
            </Button>
            {!requiredFilled && <p className="text-xs text-center text-ink/55 font-mono-spec uppercase tracking-[0.16em]">Fill the required (*) fields to unlock submit</p>}
          </form>
        </CardContent>
      </Card>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[13px] font-medium text-ink mb-2 block">{label}</Label>
      {children}
    </div>
  );
}

function RadioPills({ value, onChange, options, testidPrefix }) {
  return (
    <RadioGroup value={value} onValueChange={onChange} className="flex flex-wrap gap-2">
      {options.map(([v, label]) => (
        <label
          key={v}
          htmlFor={`${testidPrefix}-${v}`}
          data-testid={`${testidPrefix}-${v}`}
          className={`cursor-pointer px-4 py-2 rounded-full border text-sm transition ${
            value === v
              ? "bg-ink border-ink text-white"
              : "bg-white border-[var(--line)] text-ink/75 hover:border-ink/40"
          }`}
        >
          <RadioGroupItem id={`${testidPrefix}-${v}`} value={v} className="sr-only" />
          {label}
        </label>
      ))}
    </RadioGroup>
  );
}

function RadioStack({ value, onChange, options, testidPrefix }) {
  return (
    <RadioGroup value={value} onValueChange={onChange} className="space-y-2">
      {options.map(([v, label]) => (
        <label
          key={v}
          htmlFor={`${testidPrefix}-${v}`}
          data-testid={`${testidPrefix}-${v}`}
          className={`cursor-pointer flex items-center gap-3 px-4 py-3 rounded-xl border transition ${
            value === v
              ? "bg-brand-soft border-brand/40 text-ink"
              : "bg-white border-[var(--line)] text-ink/75 hover:border-ink/30"
          }`}
        >
          <RadioGroupItem id={`${testidPrefix}-${v}`} value={v} className="text-brand border-brand/50" />
          <span className="text-sm">{label}</span>
        </label>
      ))}
    </RadioGroup>
  );
}

function StarPicker({ value, onChange }) {
  return (
    <div className="flex items-center gap-1" data-testid="survey-stars">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => onChange(n)}
          data-testid={`survey-star-${n}`}
          aria-label={`${n} star${n === 1 ? "" : "s"}`}
          className="p-1.5 rounded-full transition hover:scale-110"
        >
          <Star
            size={28}
            className={n <= value ? "fill-brand text-brand" : "text-ink/25"}
          />
        </button>
      ))}
      {value > 0 && <span className="ml-2 text-sm text-ink/75 font-mono-spec">{value}/5</span>}
    </div>
  );
}

function Footer() {
  return (
    <footer className="pt-10 text-center">
      <div className="hairline max-w-md mx-auto mb-6" />
      <p className="font-mono-spec uppercase tracking-[0.22em] text-[11px] text-ink/55">Freedom In Me LLC &middot; AI Build Lab</p>
      <p className="mt-3 text-sm text-ink/65">
        Questions? <a href="mailto:info@itsthefreedominme.com" className="text-brand underline hover:no-underline">info@itsthefreedominme.com</a>
      </p>
    </footer>
  );
}

// ---------- Section divider band ----------
function SectionBand({ title }) {
  return (
    <div className="flex items-center gap-4 pt-2">
      <div className="hairline flex-1" />
      <div className="eyebrow !mb-0">{title}</div>
      <div className="hairline flex-1" />
    </div>
  );
}

// ---------- Offer tile (compact paid product card) ----------
function OfferTile({ icon, eyebrow, title, body, ctaLabel, ctaHref }) {
  const disabled = !ctaHref;
  return (
    <Card className="border-[var(--line)] bg-white rounded-2xl">
      <CardContent className="p-6">
        <div className="flex items-center gap-2 mb-3">
          <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-brand-soft text-brand">{icon}</span>
          <span className="eyebrow !mb-0">{eyebrow}</span>
        </div>
        <h3 className="font-display text-xl tracking-[-0.015em] text-ink">{title}</h3>
        <p className="mt-2 text-sm text-ink/70 leading-relaxed">{body}</p>
        <Button
          asChild={!disabled}
          disabled={disabled}
          className="btn-ink mt-4 rounded-full h-10 px-4 text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid={`offer-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
        >
          {disabled ? <span>{ctaLabel}</span> : <a href={ctaHref} target="_blank" rel="noopener noreferrer">{ctaLabel} <ArrowRight size={12} className="ml-1.5" /></a>}
        </Button>
      </CardContent>
    </Card>
  );
}

// ---------- Action tile (opens an in-app modal) ----------
function ActionTile({ icon, eyebrow, title, body, onClick, testid }) {
  return (
    <Card className="border-[var(--line)] bg-white rounded-2xl">
      <CardContent className="p-6">
        <div className="flex items-center gap-2 mb-3">
          <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-brand-soft text-brand">{icon}</span>
          <span className="eyebrow !mb-0">{eyebrow}</span>
        </div>
        <h3 className="font-display text-xl tracking-[-0.015em] text-ink">{title}</h3>
        <p className="mt-2 text-sm text-ink/70 leading-relaxed">{body}</p>
        <Button
          onClick={onClick}
          variant="outline"
          className="btn-outline-ink mt-4 rounded-full h-10 px-4 text-sm font-semibold"
          data-testid={testid}
        >
          Open form <ArrowRight size={12} className="ml-1.5" />
        </Button>
      </CardContent>
    </Card>
  );
}

// ---------- Testimonial modal ----------
function TestimonialModal({ open, setOpen }) {
  const [type, setType] = useState("written");
  const [form, setForm] = useState({ participant_name: "", email: "", business_name: "", written_testimonial: "", video_url: "", permission_granted: false });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const canSubmit = form.participant_name.trim() && form.email.trim() &&
    ((type === "written" && form.written_testimonial.trim()) || (type === "video" && form.video_url.trim()));

  async function submit(e) {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/build-lab-followup/testimonial`, { ...form, testimonial_type: type });
      setDone(true);
      toast.success("Thank you — your testimonial is in.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit");
    } finally {
      setSubmitting(false);
    }
  }

  function close() {
    setOpen(false);
    setTimeout(() => { setDone(false); setForm({ participant_name: "", email: "", business_name: "", written_testimonial: "", video_url: "", permission_granted: false }); setType("written"); }, 200);
  }

  return (
    <Dialog open={open} onOpenChange={(o) => o ? setOpen(true) : close()}>
      <DialogContent className="bg-white border border-[var(--line)] text-ink max-w-lg max-h-[90vh] overflow-y-auto" data-testid="testimonial-modal">
        {done ? (
          <div className="py-8 text-center">
            <div className="mx-auto mb-3 w-12 h-12 rounded-full bg-brand-soft flex items-center justify-center"><CheckCircle2 size={24} className="text-brand" /></div>
            <h3 className="font-display text-2xl tracking-[-0.02em]">Thank you.</h3>
            <p className="mt-3 text-ink/70">Your testimonial helps future participants take the leap.</p>
            <Button onClick={close} className="btn-ink mt-5 rounded-full h-10 px-5">Done</Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-display text-2xl tracking-[-0.025em]">Submit a testimonial</DialogTitle>
              <DialogDescription className="text-ink/65">Pick written or video, then drop your testimonial.</DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4 pt-2">
              <div className="flex gap-2">
                <button type="button" onClick={() => setType("written")} data-testid="testimonial-type-written" className={`flex-1 px-4 py-2 rounded-full border text-sm transition ${type === "written" ? "bg-ink border-ink text-white" : "bg-white border-[var(--line)] text-ink/75"}`}>Written</button>
                <button type="button" onClick={() => setType("video")} data-testid="testimonial-type-video" className={`flex-1 px-4 py-2 rounded-full border text-sm transition ${type === "video" ? "bg-ink border-ink text-white" : "bg-white border-[var(--line)] text-ink/75"}`}>Video URL</button>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <Input placeholder="Your name *" value={form.participant_name} onChange={(e) => setForm((s) => ({ ...s, participant_name: e.target.value }))} data-testid="testimonial-name" className="bg-white border-[var(--line)] focus-visible:ring-brand focus-visible:ring-offset-0" />
                <Input type="email" placeholder="Email *" value={form.email} onChange={(e) => setForm((s) => ({ ...s, email: e.target.value }))} data-testid="testimonial-email" className="bg-white border-[var(--line)] focus-visible:ring-brand focus-visible:ring-offset-0" />
              </div>
              <Input placeholder="Business name (optional)" value={form.business_name} onChange={(e) => setForm((s) => ({ ...s, business_name: e.target.value }))} data-testid="testimonial-business" className="bg-white border-[var(--line)] focus-visible:ring-brand focus-visible:ring-offset-0" />
              {type === "written" ? (
                <Textarea rows={5} placeholder="Your testimonial *" value={form.written_testimonial} onChange={(e) => setForm((s) => ({ ...s, written_testimonial: e.target.value }))} data-testid="testimonial-written" className="bg-white border-[var(--line)] focus-visible:ring-brand focus-visible:ring-offset-0" />
              ) : (
                <Input type="url" placeholder="Loom, YouTube, Drive link to your video *" value={form.video_url} onChange={(e) => setForm((s) => ({ ...s, video_url: e.target.value }))} data-testid="testimonial-video" className="bg-white border-[var(--line)] focus-visible:ring-brand focus-visible:ring-offset-0" />
              )}
              <label className="flex items-start gap-2 text-sm text-ink/80 cursor-pointer">
                <Checkbox checked={form.permission_granted} onCheckedChange={(v) => setForm((s) => ({ ...s, permission_granted: !!v }))} data-testid="testimonial-permission" className="mt-0.5 border-brand data-[state=checked]:bg-brand" />
                <span>I give Freedom In Me LLC permission to use my testimonial for marketing.</span>
              </label>
              <Button type="submit" disabled={!canSubmit || submitting} data-testid="testimonial-submit" className="btn-ink w-full rounded-full h-11 disabled:opacity-50">
                {submitting ? <Loader2 size={14} className="animate-spin" /> : "Submit testimonial"}
              </Button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ---------- Build share modal ----------
function BuildShareModal({ open, setOpen }) {
  const [form, setForm] = useState({ participant_name: "", email: "", build_title: "", build_link: "", screenshot_url: "", description: "", permission_granted: false });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const canSubmit = form.participant_name.trim() && form.email.trim() && form.build_title.trim() && form.description.trim();

  async function submit(e) {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/build-lab-followup/build-share`, form);
      setDone(true);
      toast.success("Thank you — your build is in.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit");
    } finally {
      setSubmitting(false);
    }
  }

  function close() {
    setOpen(false);
    setTimeout(() => { setDone(false); setForm({ participant_name: "", email: "", build_title: "", build_link: "", screenshot_url: "", description: "", permission_granted: false }); }, 200);
  }

  return (
    <Dialog open={open} onOpenChange={(o) => o ? setOpen(true) : close()}>
      <DialogContent className="bg-white border border-[var(--line)] text-ink max-w-lg max-h-[90vh] overflow-y-auto" data-testid="build-share-modal">
        {done ? (
          <div className="py-8 text-center">
            <div className="mx-auto mb-3 w-12 h-12 rounded-full bg-brand-soft flex items-center justify-center"><CheckCircle2 size={24} className="text-brand" /></div>
            <h3 className="font-display text-2xl tracking-[-0.02em]">Thank you for sharing.</h3>
            <p className="mt-3 text-ink/70">Donja will review your build and may feature it.</p>
            <Button onClick={close} className="btn-ink mt-5 rounded-full h-10 px-5">Done</Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-display text-2xl tracking-[-0.025em]">Share what you built</DialogTitle>
              <DialogDescription className="text-ink/65">Drop the link or a screenshot URL — we celebrate every build.</DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4 pt-2">
              <div className="grid sm:grid-cols-2 gap-3">
                <Input placeholder="Your name *" value={form.participant_name} onChange={(e) => setForm((s) => ({ ...s, participant_name: e.target.value }))} data-testid="bs-name" className="bg-white border-[var(--line)] focus-visible:ring-brand focus-visible:ring-offset-0" />
                <Input type="email" placeholder="Email *" value={form.email} onChange={(e) => setForm((s) => ({ ...s, email: e.target.value }))} data-testid="bs-email" className="bg-white border-[var(--line)] focus-visible:ring-brand focus-visible:ring-offset-0" />
              </div>
              <Input placeholder="What did you build? (title) *" value={form.build_title} onChange={(e) => setForm((s) => ({ ...s, build_title: e.target.value }))} data-testid="bs-title" className="bg-white border-[var(--line)] focus-visible:ring-brand focus-visible:ring-offset-0" />
              <Input type="url" placeholder="Live URL (optional)" value={form.build_link} onChange={(e) => setForm((s) => ({ ...s, build_link: e.target.value }))} data-testid="bs-link" className="bg-white border-[var(--line)] focus-visible:ring-brand focus-visible:ring-offset-0" />
              <Input type="url" placeholder="Screenshot URL (Imgur/Drive/Loom — optional)" value={form.screenshot_url} onChange={(e) => setForm((s) => ({ ...s, screenshot_url: e.target.value }))} data-testid="bs-screenshot" className="bg-white border-[var(--line)] focus-visible:ring-brand focus-visible:ring-offset-0" />
              <Textarea rows={4} placeholder="Tell us about it — what does it do, who is it for? *" value={form.description} onChange={(e) => setForm((s) => ({ ...s, description: e.target.value }))} data-testid="bs-description" className="bg-white border-[var(--line)] focus-visible:ring-brand focus-visible:ring-offset-0" />
              <label className="flex items-start gap-2 text-sm text-ink/80 cursor-pointer">
                <Checkbox checked={form.permission_granted} onCheckedChange={(v) => setForm((s) => ({ ...s, permission_granted: !!v }))} data-testid="bs-permission" className="mt-0.5 border-brand data-[state=checked]:bg-brand" />
                <span>I give Freedom In Me LLC permission to feature my build for marketing.</span>
              </label>
              <Button type="submit" disabled={!canSubmit || submitting} data-testid="bs-submit" className="btn-ink w-full rounded-full h-11 disabled:opacity-50">
                {submitting ? <Loader2 size={14} className="animate-spin" /> : "Share my build"}
              </Button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
